import {Attributes, AttributesRow, Button, Card, DataStatus} from 'platform/components';
import {VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {defaultTo, isEmpty, isNil} from 'ramda';

import {CoreExchangeStorageTab, useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {getWarehouseById} from '@dms/warehouse/shared';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {openInNewTab} from '../../../../../utils/openInNewTab';
import {UserName} from './UserName';

interface StorageTabProps extends RequiredTestIdProps {
  storage: CoreExchangeStorageTab | Nullish;
}

export function StorageTab(props: StorageTabProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data: warehouses, isLoading: areWarehousesLoading} = useGetWarehousesQuery();

  const formatDate = (date: string | Nullish) => {
    if (isNil(date) || isEmpty(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDateTime('dateShort', parseDate(date));
  };

  const coreItems = defaultTo([], props.storage?.coreItems);

  return (
    <DataStatus isLoading={areWarehousesLoading} isEmpty={isEmpty(coreItems)} minHeight={50}>
      <VStack spacing={4}>
        {coreItems.map((coreItem) => {
          const itemTestId = `coreItem.${coreItem.coreExchangeItemId}`;
          const delivery = coreItem.coreDeliveryByCustomer;
          const inspection = coreItem.coreInspection;
          const storage = coreItem.warehouseStorage;

          const deliveryRows: AttributesRow[] = [
            {
              label: i18n.t('entity.warehouse.labels.coreExchangeDeliveryDate'),
              value: formatDate(delivery?.deliveryDate),
            },
            {
              label: i18n.t('entity.warehouse.labels.receivedBy'),
              content: (
                <UserName
                  userId={delivery?.receivedBy}
                  data-testid={suffixTestId(`${itemTestId}.delivery.receivedBy`, props)}
                />
              ),
            },
            {
              label: i18n.t('entity.warehouse.labels.note'),
              value: delivery?.storageNote,
            },
          ];

          const inspectionRows: AttributesRow[] = [
            {
              label: i18n.t('entity.warehouse.labels.coreExchangeInspectionDate'),
              value: formatDate(inspection?.inspectedDate),
            },
            {
              label: i18n.t('entity.warehouse.labels.coreExchangeInspectedBy'),
              content: (
                <UserName
                  userId={inspection?.inspectedBy}
                  data-testid={suffixTestId(`${itemTestId}.inspection.inspectedBy`, props)}
                />
              ),
            },
            {
              label: i18n.t('entity.warehouse.labels.coreExchangeAdditionalIdentifier'),
              value: inspection?.additionalIdentifier,
            },
            {
              label: i18n.t('entity.warehouse.labels.note'),
              value: inspection?.coreInspectionNote,
            },
          ];

          const warehouseStorageRows: AttributesRow[] = [
            {
              label: i18n.t('entity.warehouse.labels.coreExchangeReceivedToWarehouseDate'),
              value: formatDate(storage?.receivedAt),
            },
            {
              label: i18n.t('entity.warehouse.labels.coreExchangeReceivedToWarehouseBy'),
              content: (
                <UserName
                  userId={storage?.receivedBy}
                  data-testid={suffixTestId(`${itemTestId}.warehouse.receivedBy`, props)}
                />
              ),
            },
            {
              label: i18n.t('entity.warehouse.labels.warehouse'),
              value: getWarehouseById(warehouses, storage?.warehouseId)?.name,
            },
            {
              label: i18n.t('entity.warehouse.labels.note'),
              value: storage?.warehouseStorageNote,
            },
            {
              label: i18n.t('entity.warehouse.labels.warehouseArticle'),
              content: isNil(storage?.articleNumber) ? (
                EMPTY_PLACEHOLDER
              ) : (
                <Button
                  variant="link"
                  size="small"
                  title={storage.articleNumber}
                  rightIcon="action/launch"
                  onClick={() =>
                    openInNewTab(
                      composePath(warehouseRoutes.articleDetailOverview, {
                        params: {warehouseId: storage.warehouseId, id: storage.articleId},
                        isSearchParamsPreserved: false,
                      })
                    )
                  }
                  data-testid={suffixTestId(`${itemTestId}.warehouse.article`, props)}
                />
              ),
            },
            {
              label: i18n.t('entity.warehouse.labels.coreExchangeWarehouseReceipt'),
              content: isNil(storage?.receiptNumber) ? (
                EMPTY_PLACEHOLDER
              ) : (
                <Button
                  variant="link"
                  size="small"
                  title={storage.receiptNumber}
                  rightIcon="action/launch"
                  onClick={() =>
                    openInNewTab(
                      composePath(warehouseRoutes.receiveNoteDetailOverview, {
                        params: {id: storage.receiveNoteId},
                        isSearchParamsPreserved: false,
                      })
                    )
                  }
                  data-testid={suffixTestId(`${itemTestId}.warehouse.receipt`, props)}
                />
              ),
            },
          ];

          return (
            <VStack key={coreItem.coreExchangeItemId} spacing={4}>
              <Card title={i18n.t('entity.warehouse.labels.coreExchangeCoreDeliveryByCustomer')}>
                <Attributes
                  rows={deliveryRows}
                  size="third"
                  data-testid={suffixTestId(`${itemTestId}.delivery`, props)}
                />
              </Card>
              <Card title={i18n.t('entity.warehouse.labels.coreExchangeCoreInspection')}>
                <Attributes
                  rows={inspectionRows}
                  size="third"
                  data-testid={suffixTestId(`${itemTestId}.inspection`, props)}
                />
              </Card>
              <Card title={i18n.t('entity.warehouse.labels.coreExchangeWarehouseStorage')}>
                <Attributes
                  rows={warehouseStorageRows}
                  size="third"
                  data-testid={suffixTestId(`${itemTestId}.warehouse`, props)}
                />
              </Card>
            </VStack>
          );
        })}
      </VStack>
    </DataStatus>
  );
}
